import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate, useParams } from 'react-router-dom';
import { dayIdToDateString } from '@/utils/toolkit';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { LeagueModel, LeagueScheduleModel } from '@/models/LeagueModels';
import { UserDashboardModel } from '@/models/UserDashboardModels';
import { resolveType } from '@/services/ServiceContainer';
import { ButtonSpinner } from '../components/LoadingSpinner';
import { calcStrokesGiven } from '../utils/handicapUtils';
import { CourseService } from '@/services/CourseService';
import { CourseModel } from '@/models/CourseModels';
import HandicapDetailsModal from './HandicapDetailsModal';

interface LeagueProps {
    league: LeagueModel;
    userDashboard: UserDashboardModel | null;
    initialDayId?: string | null;
}

const LEAGUE_TUTORIAL_KEY = 'LEAGUE_TUTORIAL_SHOWN';

const LeagueComponent: React.FC<LeagueProps> = ({ league, userDashboard: dashboardData, initialDayId }) => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const { appUser } = useUser();
    const { dayId } = useParams<{ dayId?: string }>();
    const [showPinPrizesModal, setShowPinPrizesModal] = useState<{ show: boolean; prizes: any[] }>({ show: false, prizes: [] });
    const [showTutorial, setShowTutorial] = useState(false);
    const [countdown, setCountdown] = useState(5);
    const [viewingRound, setViewingRound] = useState<string | null>(null);
    const [courseModel, setCourseModel] = useState<CourseModel | null>(null);
    const [showNoDetailsModal, setShowNoDetailsModal] = useState(false);
    const [showHandicapModal, setShowHandicapModal] = useState(false);
    const [showAdminLoginModal, setShowAdminLoginModal] = useState(false);
    const [selectedHandicapData, setSelectedHandicapData] = useState<{
        handicap: number | null;
        handicapCalcSteps: string[] | null;
        courseName?: string;
        dayId?: string;
        flightId?: string | null;
        league?: LeagueModel;
    } | null>(null);
    const tutorialTimeoutRef = useRef<NodeJS.Timeout>();
    const navigate = useNavigate();

    // Check if current user is a league admin
    const isLeagueAdmin = user && league.adminUids && league.adminUids[user.uid];

    // Check if user has admin roles in their token
    const checkAdminRoles = async (): Promise<boolean> => {
        if (!user) return false;

        try {
            const token = await user.getIdTokenResult();
            const claims = token.claims as { isAdmin?: boolean; adminLeagues?: string[] } | undefined;
            return Boolean(claims?.isAdmin) ||
                Boolean(claims?.adminLeagues && Array.isArray(claims.adminLeagues) &&
                    claims.adminLeagues.includes(league.id));
        } catch (error) {
            console.error('Error checking admin roles:', error);
            return false;
        }
    };

    const handleAdminClick = async () => {
        const hasAdminRoles = await checkAdminRoles();
        if (hasAdminRoles) {
            navigate(`/league/${league.id}/admin`);
        } else {
            setShowAdminLoginModal(true);
        }
    };

    useEffect(() => {
        const fetchCourse = async () => {
            if (!league.courseId) return;
            try {
                const courseService = resolveType(CourseService);
                const courseModel = await courseService.get(league.courseId);
                if (courseModel) {
                    setCourseModel(courseModel);
                }
            } catch (error) {
                console.error('Error loading course:', error);
            }
        };

        fetchCourse();
    }, [league.courseId]);

    useEffect(() => {
        const hasSeenTutorial = localStorage.getItem(LEAGUE_TUTORIAL_KEY);
        if (!hasSeenTutorial) {
            // Small delay to ensure the DOM is ready
            tutorialTimeoutRef.current = setTimeout(() => {
                setShowTutorial(true);
            }, 500);
        }

        return () => {
            if (tutorialTimeoutRef.current) {
                clearTimeout(tutorialTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (showTutorial) {
            const timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        setShowTutorial(false);
                        localStorage.setItem(LEAGUE_TUTORIAL_KEY, 'true');
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [showTutorial]);

    // Find the index of the first schedule item with a date >= today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let initialSlide = 0;

    // If dayId is provided in URL params, find that specific day
    if (dayId) {
        const dayIndex = league.schedule.findIndex(schedule => schedule.dayId === dayId);
        if (dayIndex !== -1) {
            initialSlide = dayIndex;
        }
    } else if (initialDayId) {
        // Fallback to initialDayId prop if provided
        const dayIndex = league.schedule.findIndex(schedule => schedule.dayId === initialDayId);
        if (dayIndex !== -1) {
            initialSlide = dayIndex;
        }
    } else {
        // First try to find the last active item
        const activeItemIndex = league.schedule.findIndex(schedule => schedule.status === 'active');
        if (activeItemIndex !== -1) {
            initialSlide = activeItemIndex;
        } else {
            // If no active items, find the first item from today onwards
            for (let i = 0; i < league.schedule.length; i++) {
                const [year, month, day] = dayIdToDateString(league.schedule[i].dayId).split('-');
                const itemDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                if (itemDate >= today) {
                    initialSlide = i;
                    break;
                }
            }
        }
    }

    const settings = {
        centerMode: true,
        centerPadding: '40px',
        dots: true,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        swipeToSlide: true,
        arrows: false,
        initialSlide,
        afterChange: (currentSlide: number) => {
            if (showTutorial) {
                localStorage.setItem(LEAGUE_TUTORIAL_KEY, 'true');
                setShowTutorial(false);
            }

            // Update URL with the current dayId
            const currentDayId = league.schedule[currentSlide]?.dayId;
            if (currentDayId) {
                navigate(`/dashboard/${currentDayId}`);
            }
        },
        appendDots: (dots: React.ReactNode) => (
            <ul style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'flex-end',
                marginTop: 24,
                marginBottom: 24,
                padding: 0,
                listStyle: 'none',
                maxWidth: '100%',
                overflow: 'visible',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                zIndex: 2,
            }}>{dots}</ul>
        ),
        responsive: [
            {
                breakpoint: 600,
                settings: {
                    centerPadding: '16px',
                }
            }
        ]
    };

    const formatDate = (dayId: string) => {
        const [year, month, day] = dayIdToDateString(dayId).split('-');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDuration = (startTime: Timestamp | null, endTime: Timestamp): string => {
        const start = startTime ? startTime.seconds * 1000 : endTime.seconds * 1000 - 60000; // fallback to 1 min before end
        const end = endTime.seconds * 1000;
        const durationMs = end - start;

        const hours = Math.floor(durationMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) {
            return `${hours} hr${hours > 1 ? 's' : ''} ${minutes} min`;
        }
        return `${minutes} min`;
    };

    const getDuration = (matchingRound: any): string => {
        if (!matchingRound.startedAt) return '';

        const endTime = matchingRound.playedAt || Timestamp.now();
        const duration = formatDuration(matchingRound.startedAt, endTime);

        // Check if the duration contains NaN and return empty string if it does
        if (duration.includes('NaN')) return '';

        return duration;
    };

    // Find the matching round for the current schedule item
    const getMatchingRound = (schedule: LeagueScheduleModel) => {
        return dashboardData?.leagueRounds?.[league.id]?.rounds?.find(round =>
            round.setup?.league?.dayId === schedule.dayId
        );
    };

    // Update handicap when matching round changes
    // useEffect(() => {
    //     const fetchHandicap = async () => {
    //         const activeSchedule = league.schedule.find(s => s.status === 'active');
    //         if (activeSchedule && user) {
    //             const matchingRound = getMatchingRound(activeSchedule);
    //         }
    //     };
    //     fetchHandicap();
    // }, [league.schedule, user, dashboardData]);

    const renderScheduleItem = (schedule: LeagueScheduleModel) => {
        let holesLabel = '';
        if (schedule.holesToPlay === 'front9') holesLabel = 'Front 9';
        else if (schedule.holesToPlay === 'back9') holesLabel = 'Back 9';
        else if (schedule.holesToPlay === 'all18') holesLabel = 'All 18';

        // Find the matching round in the dashboard data
        const matchingRound = getMatchingRound(schedule);
        const hasExistingRound = !!matchingRound;

        // if this is an older document in firebase, it won't have par property. so let's backfill it.
        if (matchingRound && !matchingRound.par) {
            matchingRound.par = matchingRound.holes.reduce((sum, hole) => sum + (hole.par || 0), 0);
        }

        return (
            <section style={{ width: '100%', margin: 0, padding: 0, boxSizing: 'border-box' }}>
                <div style={{
                    width: '100%',
                    border: `1.5px solid ${theme.colors.BORDER}`,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
                    borderRadius: 16,
                    background: schedule.status === 'active' || schedule.status === 'locked' ? theme.colors.HIGHLIGHTED_ITEM : theme.colors.BACKGROUND,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginBottom: 96,
                }}>
                    <div style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: '24px 28px 0 28px',
                    }}>
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            alignItems: 'flex-start',
                        }}>
                            <h3 style={{
                                color: theme.colors.TEXT,
                                fontSize: '1.5em',
                                fontWeight: 'bold',
                                margin: 0,
                            }}>
                                {formatDate(schedule.dayId)}
                            </h3>
                        </div>
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <button
                                style={{
                                    background: matchingRound?.handicap !== null && matchingRound?.handicapCalcSteps
                                        ? theme.colors.SURFACE
                                        : 'transparent',
                                    border: matchingRound?.handicap !== null && matchingRound?.handicapCalcSteps
                                        ? `1px solid ${theme.colors.BORDER}`
                                        : '1px solid transparent',
                                    color: matchingRound?.handicap !== null && matchingRound?.handicapCalcSteps
                                        ? theme.colors.TEXT
                                        : theme.colors.TEXT_SECONDARY,
                                    fontWeight: 600,
                                    fontSize: '1em',
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    cursor: matchingRound?.handicap !== null && matchingRound?.handicapCalcSteps ? 'pointer' : 'default',
                                    outline: 'none',
                                    boxShadow: matchingRound?.handicap !== null && matchingRound?.handicapCalcSteps
                                        ? '0 2px 4px rgba(0,0,0,0.1)'
                                        : 'none',
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    textAlign: 'center',
                                }}
                                onClick={() => {
                                    if (matchingRound?.handicap !== null && matchingRound?.handicapCalcSteps) {
                                        setSelectedHandicapData({
                                            handicap: matchingRound.handicap,
                                            handicapCalcSteps: matchingRound.handicapCalcSteps,
                                            courseName: courseModel?.name,
                                            dayId: schedule.dayId,
                                            flightId: matchingRound.flightId,
                                            league: league
                                        });
                                        setShowHandicapModal(true);
                                    }
                                }}
                                disabled={matchingRound?.handicap === null || !matchingRound?.handicapCalcSteps}
                            >
                                Hcp: {matchingRound?.handicap || '-'}
                            </button>
                        </div>
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                        }}>
                            <div style={{
                                color: theme.colors.TEXT_SECONDARY,
                                fontSize: '1.1em',
                                fontWeight: 500,
                            }}>{holesLabel}</div>
                        </div>
                    </div>
                    {matchingRound ? (
                        <>
                            {/* Hole scores display (restore original row style) */}
                            {matchingRound.holesToPlay !== schedule.holesToPlay && (
                                <div style={{
                                    width: '100%',
                                    textAlign: 'center',
                                    color: theme.colors.ERROR,
                                    fontSize: '1.1em',
                                    fontWeight: 500,
                                    marginBottom: 12,
                                    padding: '0 24px'
                                }}>
                                    {matchingRound.holesToPlay === 'back9' ? 'Back 9' : 'Front 9'}
                                </div>
                            )}
                            <div style={{
                                width: '100%',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '2px',
                                padding: '0 4px',
                                marginBottom: 12,
                                justifyContent: 'center',
                            }}>
                                {(matchingRound?.holesToPlay === 'back9'
                                    ? Array.from({ length: 9 }, (_, i) => i + 10)
                                    : Array.from({ length: 9 }, (_, i) => i + 1)
                                ).map((hole) => {
                                    const holeStats = ((matchingRound as any).holes || []).find((stat: any) => stat.hole === hole);
                                    const grossScore = holeStats?.grossScore;
                                    const par = holeStats?.par;
                                    let bgColor = 'transparent';
                                    let textColor = theme.colors.TEXT;
                                    if (grossScore !== undefined && grossScore !== null && par !== undefined && par !== null) {
                                        if (grossScore < par) {
                                            bgColor = theme.colors.UNDER_PAR;
                                            textColor = theme.colors.UNDER_PAR_TEXT;
                                        } else if (grossScore === par) {
                                            bgColor = theme.colors.PAR;
                                            textColor = theme.colors.PAR_TEXT;
                                        } else if (grossScore > par) {
                                            bgColor = theme.colors.OVER_PAR;
                                            textColor = theme.colors.OVER_PAR_TEXT;
                                        }
                                    }
                                    let borderRadius = '4px';
                                    if (grossScore !== undefined && grossScore !== null && par !== undefined && par !== null) {
                                        if (grossScore < par) {
                                            borderRadius = '50%'; // under par
                                        } else if (grossScore === par) {
                                            borderRadius = '0'; // par
                                        } else if (grossScore > par) {
                                            borderRadius = '4px'; // over par
                                        }
                                    }
                                    // Dots logic
                                    let numDots = 0;
                                    if (typeof matchingRound?.handicap === 'number' && courseModel) {
                                        const holeInfo = courseModel.scorecard.holes.find(h => h.number === hole);
                                        if (holeInfo) {
                                            numDots = calcStrokesGiven(matchingRound?.handicap, holeInfo, matchingRound.holesToPlay);
                                        }
                                    }
                                    return (
                                        <div key={hole} style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '2px',
                                            paddingTop: '10px'
                                        }}>
                                            {/* Dots above hole number if strokes given > 0 */}
                                            <div style={{ height: '10px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '2px' }}>
                                                {numDots > 0 && Array.from({ length: numDots }).map((_, i) => (
                                                    <span key={i} style={{
                                                        display: 'inline-block',
                                                        width: '7px',
                                                        height: '7px',
                                                        borderRadius: '50%',
                                                        background: theme.colors.BLACK,
                                                        marginBottom: '2px',
                                                        boxShadow: '0 0 0 1px #fff'
                                                    }} />
                                                ))}
                                            </div>
                                            <div style={{
                                                fontSize: '0.8em',
                                                color: theme.colors.TEXT_SECONDARY,
                                            }}>
                                                {hole}
                                            </div>
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: bgColor,
                                                border: `1px solid ${theme.colors.BORDER}`,
                                                borderRadius: borderRadius,
                                                color: textColor,
                                                fontWeight: 'bold',
                                            }}>
                                                {grossScore || '-'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {/* Score display (restore above holes) */}
                            <div style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginTop: theme.spacing.LARGE,
                                maxWidth: '400px',
                                margin: '24px auto 0',
                                padding: '0 24px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.MEDIUM,
                                    padding: '0 12px'
                                }}>
                                    <span style={{
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontWeight: 'bold'
                                    }}>Gross:</span>
                                    <span style={{
                                        color: theme.colors.TEXT,
                                        fontWeight: 'bold',
                                        fontSize: '1.2em'
                                    }}>
                                        {(() => {
                                            // Only sum holes that have been played
                                            const playedHoles = (matchingRound as any).holes?.filter((hole: any) =>
                                                hole.grossScore !== undefined && hole.grossScore !== null
                                            ) || [];
                                            return playedHoles.length > 0
                                                ? playedHoles.reduce((sum: number, hole: any) => sum + hole.grossScore, 0)
                                                : '-';
                                        })()}
                                    </span>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.MEDIUM
                                }}>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: (() => {
                                            // Only consider played holes for relative to par
                                            const playedHoles = (matchingRound as any).holes?.filter((hole: any) =>
                                                hole.grossScore !== undefined && hole.grossScore !== null &&
                                                hole.par !== undefined && hole.par !== null
                                            ) || [];
                                            if (playedHoles.length === 0) return theme.colors.TEXT;

                                            const totalGross = playedHoles.reduce((sum: number, hole: any) => sum + hole.grossScore, 0);
                                            const totalPar = playedHoles.reduce((sum: number, hole: any) => sum + hole.par, 0);
                                            const relativeToPar = totalGross - totalPar;
                                            return relativeToPar === 0 ? theme.colors.SUCCESS : theme.colors.WHITE;
                                        })(),
                                        backgroundColor: (() => {
                                            // Only consider played holes for relative to par
                                            const playedHoles = (matchingRound as any).holes?.filter((hole: any) =>
                                                hole.grossScore !== undefined && hole.grossScore !== null &&
                                                hole.par !== undefined && hole.par !== null
                                            ) || [];
                                            if (playedHoles.length === 0) return 'transparent';

                                            const totalGross = playedHoles.reduce((sum: number, hole: any) => sum + hole.grossScore, 0);
                                            const totalPar = playedHoles.reduce((sum: number, hole: any) => sum + hole.par, 0);
                                            const relativeToPar = totalGross - totalPar;
                                            return relativeToPar < 0 ? theme.colors.ERROR :
                                                relativeToPar > 0 ? theme.colors.OVER_PAR : 'transparent';
                                        })(),
                                        fontWeight: 'bold',
                                        fontSize: '1.2em',
                                        width: (() => {
                                            // Only consider played holes for relative to par
                                            const playedHoles = (matchingRound as any).holes?.filter((hole: any) =>
                                                hole.grossScore !== undefined && hole.grossScore !== null &&
                                                hole.par !== undefined && hole.par !== null
                                            ) || [];
                                            if (playedHoles.length === 0) return 'auto';

                                            const totalGross = playedHoles.reduce((sum: number, hole: any) => sum + hole.grossScore, 0);
                                            const totalPar = playedHoles.reduce((sum: number, hole: any) => sum + hole.par, 0);
                                            const relativeToPar = totalGross - totalPar;
                                            return relativeToPar === 0 ? 'auto' : '40px';
                                        })(),
                                        height: (() => {
                                            // Only consider played holes for relative to par
                                            const playedHoles = (matchingRound as any).holes?.filter((hole: any) =>
                                                hole.grossScore !== undefined && hole.grossScore !== null &&
                                                hole.par !== undefined && hole.par !== null
                                            ) || [];
                                            if (playedHoles.length === 0) return 'auto';

                                            const totalGross = playedHoles.reduce((sum: number, hole: any) => sum + hole.grossScore, 0);
                                            const totalPar = playedHoles.reduce((sum: number, hole: any) => sum + hole.par, 0);
                                            const relativeToPar = totalGross - totalPar;
                                            return relativeToPar === 0 ? 'auto' : '40px';
                                        })(),
                                        borderRadius: (() => {
                                            // Only consider played holes for relative to par
                                            const playedHoles = (matchingRound as any).holes?.filter((hole: any) =>
                                                hole.grossScore !== undefined && hole.grossScore !== null &&
                                                hole.par !== undefined && hole.par !== null
                                            ) || [];
                                            if (playedHoles.length === 0) return '4px';

                                            const totalGross = playedHoles.reduce((sum: number, hole: any) => sum + hole.grossScore, 0);
                                            const totalPar = playedHoles.reduce((sum: number, hole: any) => sum + hole.par, 0);
                                            const relativeToPar = totalGross - totalPar;
                                            return relativeToPar < 0 ? '50%' : '4px';
                                        })(),
                                        padding: (() => {
                                            // Only consider played holes for relative to par
                                            const playedHoles = (matchingRound as any).holes?.filter((hole: any) =>
                                                hole.grossScore !== undefined && hole.grossScore !== null &&
                                                hole.par !== undefined && hole.par !== null
                                            ) || [];
                                            if (playedHoles.length === 0) return '0';

                                            const totalGross = playedHoles.reduce((sum: number, hole: any) => sum + hole.grossScore, 0);
                                            const totalPar = playedHoles.reduce((sum: number, hole: any) => sum + hole.par, 0);
                                            const relativeToPar = totalGross - totalPar;
                                            return relativeToPar === 0 ? '0 8px' : '0';
                                        })(),
                                        margin: '0 4px',
                                        lineHeight: '24px'
                                    }}>
                                        {(() => {
                                            // Only consider played holes for relative to par
                                            const playedHoles = (matchingRound as any).holes?.filter((hole: any) =>
                                                hole.grossScore !== undefined && hole.grossScore !== null &&
                                                hole.par !== undefined && hole.par !== null
                                            ) || [];
                                            if (playedHoles.length === 0) return '-';

                                            const totalGross = playedHoles.reduce((sum: number, hole: any) => sum + hole.grossScore, 0);
                                            const totalPar = playedHoles.reduce((sum: number, hole: any) => sum + hole.par, 0);
                                            const relativeToPar = totalGross - totalPar;
                                            if (relativeToPar === 0) return 'E';
                                            return relativeToPar > 0 ? `+${relativeToPar}` : relativeToPar;
                                        })()}
                                    </span>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.MEDIUM
                                }}>
                                    <span style={{
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontWeight: 'bold'
                                    }}>Net:</span>
                                    <span style={{
                                        color: theme.colors.TEXT,
                                        fontWeight: 'bold',
                                        fontSize: '1.2em'
                                    }}>
                                        {(() => {
                                            // Only sum holes that have been played
                                            const playedHoles = (matchingRound as any).holes?.filter((hole: any) =>
                                                hole.netScore !== undefined && hole.netScore !== null
                                            ) || [];
                                            return playedHoles.length > 0
                                                ? playedHoles.reduce((sum: number, hole: any) => sum + hole.netScore, 0)
                                                : '-';
                                        })()}
                                    </span>
                                </div>
                            </div>
                            {/* Stats totals display */}
                            {matchingRound?.holes?.some((stat: any) =>
                                (stat.fairwayHit !== undefined && stat.fairwayHit !== null) ||
                                (stat.greenInRegulation !== undefined && stat.greenInRegulation !== null) ||
                                (stat.chips !== undefined && stat.chips !== null) ||
                                (stat.putts !== undefined && stat.putts !== null)
                            ) && (
                                    <div style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginTop: theme.spacing.MEDIUM,
                                        maxWidth: '400px',
                                        margin: '12px auto',
                                        padding: '0 24px'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <span style={{
                                                color: theme.colors.TEXT_SECONDARY,
                                                fontSize: '0.9em'
                                            }}>Fairway</span>
                                            <span style={{
                                                color: theme.colors.TEXT,
                                                fontWeight: 'bold'
                                            }}>
                                                {(matchingRound as any)?.holes?.some((stat: any) => stat.fairwayHit !== undefined && stat.fairwayHit !== null)
                                                    ? (matchingRound as any)?.holes?.filter((stat: any) => stat.fairwayHit === true).length || '0'
                                                    : '-'
                                                }
                                            </span>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <span style={{
                                                color: theme.colors.TEXT_SECONDARY,
                                                fontSize: '0.9em'
                                            }}>GIR</span>
                                            <span style={{
                                                color: theme.colors.TEXT,
                                                fontWeight: 'bold'
                                            }}>
                                                {(matchingRound as any)?.holes?.some((stat: any) => stat.greenInRegulation !== undefined && stat.greenInRegulation !== null)
                                                    ? (matchingRound as any)?.holes?.filter((stat: any) => stat.greenInRegulation === true).length || '0'
                                                    : '-'
                                                }
                                            </span>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <span style={{
                                                color: theme.colors.TEXT_SECONDARY,
                                                fontSize: '0.9em'
                                            }}>Chips</span>
                                            <span style={{
                                                color: theme.colors.TEXT,
                                                fontWeight: 'bold'
                                            }}>
                                                {(matchingRound as any)?.holes?.some((stat: any) => stat.chips !== undefined && stat.chips !== null)
                                                    ? (matchingRound as any)?.holes?.reduce((total: number, stat: any) => total + (stat.chips || 0), 0) || '0'
                                                    : '-'
                                                }
                                            </span>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <span style={{
                                                color: theme.colors.TEXT_SECONDARY,
                                                fontSize: '0.9em'
                                            }}>Putts</span>
                                            <span style={{
                                                color: theme.colors.TEXT,
                                                fontWeight: 'bold'
                                            }}>
                                                {(matchingRound as any)?.holes?.some((stat: any) => stat.putts !== undefined && stat.putts !== null)
                                                    ? (matchingRound as any)?.holes?.reduce((total: number, stat: any) => total + (stat.putts || 0), 0) || '0'
                                                    : '-'
                                                }
                                            </span>
                                        </div>
                                    </div>
                                )}
                        </>
                    ) : (
                        <div style={{
                            width: '100%',
                            textAlign: 'center',
                            color: schedule.status === 'cancelled' ? theme.colors.ERROR : theme.colors.TEXT_SECONDARY,
                            fontWeight: 500,
                            fontSize: '1.1em',
                            margin: '16px 0',
                        }}>
                            {schedule.status === 'cancelled' ? 'CANCELLED' : 'No score recorded'}
                        </div>
                    )}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        marginTop: 12,
                    }}>
                        {schedule.status === 'locked' ? (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                gap: '8px',
                                marginTop: 8,
                                marginBottom: 8,
                                color: theme.colors.TEXT_SECONDARY,
                                fontSize: '1em',
                                fontWeight: 500,
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" />
                                </svg>
                                Locked - Calculating
                            </div>
                        ) : (
                            <>
                                {schedule.pinPrizes && schedule.pinPrizes.length > 0 && (schedule.status === 'active' || schedule.status === 'notstarted') && (
                                    <button
                                        onClick={() => setShowPinPrizesModal({ show: true, prizes: schedule.pinPrizes })}
                                        style={{
                                            background: theme.colors.SURFACE,
                                            border: `1px solid ${theme.colors.BORDER}`,
                                            color: theme.colors.TEXT,
                                            fontWeight: 600,
                                            fontSize: '1em',
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            padding: '12px 24px',
                                            borderRadius: '8px',
                                            marginLeft: 18,
                                            marginTop: 8,
                                            marginBottom: 8,
                                            outline: 'none',
                                        }}
                                    >
                                        Pin Prizes
                                    </button>
                                )}
                                {schedule.status === 'active' && (
                                    <button
                                        onClick={() => {
                                            if (hasExistingRound && matchingRound?.status !== 'active') {
                                                // Check if roundId is null, empty, or undefined
                                                if (!matchingRound?.roundId || matchingRound.roundId === '') {
                                                    setShowNoDetailsModal(true);
                                                } else {
                                                    handlePlayClick(schedule.dayId);
                                                }
                                            } else {
                                                handlePlayClick(schedule.dayId);
                                            }
                                        }}
                                        style={{
                                            background: theme.colors.BUTTON_PRIMARY_BG,
                                            color: theme.colors.BUTTON_PRIMARY_TEXT,
                                            fontWeight: 'bold',
                                            fontSize: '1em',
                                            marginLeft: 'auto',
                                            marginRight: 18,
                                            marginTop: 8,
                                            marginBottom: 8,
                                            padding: '12px 24px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            cursor: viewingRound === schedule.dayId ? 'default' : 'pointer',
                                            outline: 'none',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                            opacity: viewingRound === schedule.dayId ? 0.7 : 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: theme.spacing.SMALL,
                                            minWidth: '100px'
                                        }}
                                        disabled={viewingRound === schedule.dayId}
                                    >
                                        {viewingRound === schedule.dayId ? (
                                            <ButtonSpinner size={20} color="white" />
                                        ) : hasExistingRound ? (matchingRound?.status === 'active' ? 'Continue' : 'Details') : 'Play'}
                                    </button>
                                )}
                                {schedule.status === 'completed' && (
                                    <button
                                        onClick={() => handleResultsClick(schedule.dayId)}
                                        style={{
                                            background: theme.colors.BUTTON_PRIMARY_BG,
                                            color: theme.colors.BUTTON_PRIMARY_TEXT,
                                            fontWeight: 'bold',
                                            fontSize: '1em',
                                            marginLeft: 'auto',
                                            marginRight: 18,
                                            marginTop: 8,
                                            marginBottom: 8,
                                            padding: '12px 24px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            cursor: 'pointer',
                                            outline: 'none',
                                        }}
                                    >
                                        Results
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                    {/* Food Being Served Display */}
                    {(schedule.foodBeingServed || schedule.foodBeingServedImageUrl) && (
                        <div style={{
                            width: '100%',
                            textAlign: 'center',
                            color: theme.colors.PRIMARY,
                            fontWeight: 700,
                            fontSize: '1.5em',
                            margin: '12px 0 0 0',
                            letterSpacing: '0.5px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px',
                            padding: '8px'
                        }}>
                            {schedule.foodBeingServedImageUrl &&
                                <img
                                    src={schedule.foodBeingServedImageUrl || ''}
                                    alt="Food Being Served"
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '200px',
                                        width: 'auto',
                                        height: 'auto',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }}
                                />
                            }
                            {schedule.foodBeingServed &&
                                <p>{schedule.foodBeingServed}</p>
                            }
                        </div>
                    )}
                    {matchingRound?.startedAt && (
                        <div style={{
                            color: theme.colors.TEXT_SECONDARY,
                            fontSize: '0.9em',
                            textAlign: 'right',
                            marginRight: 18,
                            marginTop: 4,
                            marginBottom: 8,
                        }}>
                            {getDuration(matchingRound)}
                        </div>
                    )}
                    {matchingRound?.playedAt && matchingRound.status === 'completed' && (
                        <div style={{
                            color: theme.colors.TEXT_SECONDARY,
                            fontSize: '0.9em',
                            textAlign: 'right',
                            marginRight: 18,
                            marginTop: 4,
                            marginBottom: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '4px'
                        }}>
                            <span style={{ fontSize: '1em' }}>✅</span>
                            {(() => {
                                const playedAt = matchingRound.playedAt;
                                if (!playedAt) return '-';
                                let dateObj;
                                if (typeof playedAt.toDate === 'function') {
                                    dateObj = playedAt.toDate();
                                } else if (playedAt.seconds) {
                                    dateObj = new Date(playedAt.seconds * 1000);
                                } else {
                                    return '-';
                                }
                                return `Submitted ${dateObj.toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                })}`;
                            })()}
                        </div>
                    )}
                </div>
            </section>
        );
    };

    const handlePlayClick = async (dayId: string) => {
        if (!user) {
            console.error('No user found');
            return;
        }

        // Find the existing round if it exists
        const existingRound = dashboardData?.leagueRounds?.[league.id]?.rounds?.find(round =>
            round.setup?.league?.dayId === dayId
        );

        if (existingRound) {
            try {
                setViewingRound(dayId);
                navigate(`/round-scorecard/${league.courseId}/${existingRound.roundId}`);

            } catch (error) {
                console.error('Error updating round status:', error);
                setViewingRound(null);
                // Still navigate even if update fails
                const entryMode = localStorage.getItem('entryMode') || 'holeByHole';

                if (entryMode === 'scoreSummary') {
                    navigate(`/scores-only-entry/${league.courseId}/${existingRound.roundId}`);
                } else {
                    const startingHole = existingRound.holesToPlay === 'back9' ? 10 : 1;
                    navigate(`/hole-entry/${league.courseId}/${existingRound.roundId}/${startingHole}`);
                }
            }
        } else {
            // Get entry mode from localStorage
            const entryMode = localStorage.getItem('entryMode') || 'holeByHole';

            // Navigate to start round page for new rounds. Replace the current route to avoid back navigation.
            if (entryMode === 'scoreSummary') {
                navigate(`/league/${league.id}/start-round/${league.courseId}/${dayId}?entryMode=scoreSummary`, { replace: true });
            } else {
                navigate(`/league/${league.id}/start-round/${league.courseId}/${dayId}`, { replace: true });
            }
        }
    };

    const handleResultsClick = (dayId: string) => {
        // Get user's flightId from league members
        const userFlightId = league.members[appUser?.encodedEmail || '']?.flightId;
        const url = userFlightId ? `/league-results/${league.id}_${dayId}/${userFlightId}` : `/league-results/${league.id}_${dayId}`;
        navigate(url);
    };

    return (
        <div style={{
            backgroundColor: theme.colors.BACKGROUND,
            borderRadius: theme.borderRadius.MEDIUM,
            border: `1px solid ${theme.colors.BORDER}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            marginBottom: theme.spacing.LARGE,
            position: 'relative',
            width: '100%'
        }}>
            <h2 style={{
                color: theme.colors.TEXT,
                marginBottom: theme.spacing.LARGE,
                fontSize: '1.5em',
                fontWeight: 'bold',
                padding: theme.spacing.MEDIUM
            }}>
                {league.name}
            </h2>
            {/* Carousel Outer Wrapper to contain bleed */}
            <div style={{
                width: '100%',
                overflowX: 'hidden',
                position: 'relative',
            }}>
                <div style={{
                    marginTop: theme.spacing.LARGE,
                    width: '100%',
                    margin: 0,
                    boxSizing: 'border-box',
                    overflow: 'visible',
                    position: 'relative',
                }}>
                    <style>{`
                      .league-carousel .slick-list { overflow: visible !important; }
                      .league-carousel .slick-dots li button:before {
                        font-size: 18px !important;
                        color: #888;
                      }
                      .league-carousel .slick-dots li.slick-active button:before {
                        color: #222;
                      }
                    `}</style>
                    <Slider {...{
                        ...settings,
                    }} className="league-carousel">
                        {league.schedule.map((schedule: LeagueScheduleModel, index: number) => (
                            <div key={index} data-index={index}>
                                {renderScheduleItem(schedule)}
                            </div>
                        ))}
                    </Slider>
                </div>
            </div>

            {/* Admin and Reports Buttons - now in their own row below the carousel */}
            {isLeagueAdmin && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: theme.spacing.MEDIUM,
                    marginBottom: theme.spacing.SMALL,
                    paddingLeft: theme.spacing.MEDIUM,
                    paddingRight: theme.spacing.MEDIUM,
                }}>
                    <button
                        onClick={handleAdminClick}
                        style={{
                            background: theme.colors.SURFACE,
                            border: `1px solid ${theme.colors.BORDER}`,
                            color: theme.colors.TEXT,
                            fontWeight: 600,
                            fontSize: '1em',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        Admin
                    </button>
                    <button
                        onClick={() => navigate(`/league-reports/${league.id}`)}
                        style={{
                            background: theme.colors.SURFACE,
                            border: `1px solid ${theme.colors.BORDER}`,
                            color: theme.colors.TEXT,
                            fontWeight: 600,
                            fontSize: '1em',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        Reports
                    </button>
                </div>
            )}

            {showTutorial && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none'
                }}>
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1001,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: theme.spacing.MEDIUM,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        padding: theme.spacing.LARGE,
                        borderRadius: theme.borderRadius.MEDIUM,
                        pointerEvents: 'none'
                    }}>
                        <div style={{
                            fontSize: '4em',
                            color: '#FFFFFF',
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            animation: 'slideRight 2s infinite'
                        }}>
                            👆
                        </div>
                        <div style={{
                            color: '#FFFFFF',
                            textAlign: 'center',
                            fontSize: '1.2em',
                            fontWeight: 'bold',
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}>
                            Swipe to see more league dates
                        </div>
                        <div style={{
                            color: '#FFFFFF',
                            textAlign: 'center',
                            fontSize: '1em',
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}>
                            Tutorial will dismiss in {countdown} seconds
                        </div>
                    </div>
                </div>
            )}

            {/* No Details Modal */}
            {showNoDetailsModal && (
                <div
                    onClick={() => setShowNoDetailsModal(false)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: theme.colors.BACKGROUND,
                            padding: theme.spacing.LARGE,
                            borderRadius: theme.borderRadius.MEDIUM,
                            maxWidth: '90%',
                            width: '400px',
                            position: 'relative',
                        }}
                    >
                        <button
                            onClick={() => setShowNoDetailsModal(false)}
                            style={{
                                position: 'absolute',
                                top: theme.spacing.MEDIUM,
                                right: theme.spacing.MEDIUM,
                                background: 'none',
                                border: 'none',
                                fontSize: '1.5em',
                                cursor: 'pointer',
                                color: theme.colors.TEXT,
                            }}
                        >
                            ×
                        </button>
                        <h3 style={{
                            color: theme.colors.TEXT,
                            marginBottom: theme.spacing.MEDIUM,
                            fontSize: '1.3em',
                            fontWeight: 'bold',
                            textAlign: 'center',
                        }}>
                            No Round Details Available
                        </h3>
                        <p style={{
                            color: theme.colors.TEXT_SECONDARY,
                            textAlign: 'center',
                            lineHeight: '1.5',
                            margin: 0,
                        }}>
                            This round has no details to view as it was imported from an external source.
                        </p>
                    </div>
                </div>
            )}

            <style>
                {`
                    @keyframes slideRight {
                        0% { transform: translateX(-20px); }
                        50% { transform: translateX(20px); }
                        100% { transform: translateX(-20px); }
                    }
                `}
            </style>

            {/* Pin Prizes Modal */}
            {showPinPrizesModal.show && (
                <div
                    onClick={() => setShowPinPrizesModal({ show: false, prizes: [] })}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            background: theme.colors.BACKGROUND,
                            padding: theme.spacing.LARGE,
                            borderRadius: theme.borderRadius.MEDIUM,
                            maxWidth: '90%',
                            width: '500px',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            position: 'relative',
                        }}
                    >
                        <button
                            onClick={() => setShowPinPrizesModal({ show: false, prizes: [] })}
                            style={{
                                position: 'absolute',
                                top: theme.spacing.MEDIUM,
                                right: theme.spacing.MEDIUM,
                                background: 'none',
                                border: 'none',
                                fontSize: '1.5em',
                                cursor: 'pointer',
                                color: theme.colors.TEXT,
                            }}
                        >
                            ×
                        </button>
                        <h3 style={{
                            color: theme.colors.TEXT,
                            marginBottom: theme.spacing.MEDIUM,
                            fontSize: '1.5em',
                            fontWeight: 'bold',
                        }}>
                            Pin Prizes
                        </h3>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: theme.spacing.MEDIUM,
                        }}>
                            {showPinPrizesModal.prizes.map((prize, idx) => (
                                <div key={idx} style={{
                                    background: theme.colors.SURFACE,
                                    borderRadius: theme.borderRadius.SMALL,
                                    border: `1px solid ${theme.colors.BORDER}`,
                                    padding: theme.spacing.MEDIUM,
                                }}>
                                    <div style={{ fontWeight: 500, color: theme.colors.TEXT }}>
                                        {prize.description}
                                    </div>
                                    <div style={{
                                        fontSize: '0.95em',
                                        color: theme.colors.TEXT_SECONDARY,
                                        marginTop: '4px',
                                    }}>
                                        Hole {prize.hole}{prize.teeName ? ` • Tee: ${prize.teeName}` : ''}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Handicap Details Modal */}
            <HandicapDetailsModal
                isOpen={showHandicapModal}
                onClose={() => {
                    setShowHandicapModal(false);
                    setSelectedHandicapData(null);
                }}
                handicap={selectedHandicapData?.handicap || null}
                handicapCalcSteps={selectedHandicapData?.handicapCalcSteps || null}
                courseName={selectedHandicapData?.courseName}
                dayId={selectedHandicapData?.dayId}
                flightId={selectedHandicapData?.flightId}
                league={selectedHandicapData?.league}
            />

            {/* Admin Login Modal */}
            {showAdminLoginModal && (
                <div
                    onClick={() => setShowAdminLoginModal(false)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: theme.colors.BACKGROUND,
                            padding: theme.spacing.LARGE,
                            borderRadius: theme.borderRadius.MEDIUM,
                            maxWidth: '90%',
                            width: '400px',
                            position: 'relative',
                        }}
                    >
                        <button
                            onClick={() => setShowAdminLoginModal(false)}
                            style={{
                                position: 'absolute',
                                top: theme.spacing.MEDIUM,
                                right: theme.spacing.MEDIUM,
                                background: 'none',
                                border: 'none',
                                fontSize: '1.5em',
                                cursor: 'pointer',
                                color: theme.colors.TEXT,
                            }}
                        >
                            ×
                        </button>
                        <h3 style={{
                            color: theme.colors.TEXT,
                            marginBottom: theme.spacing.MEDIUM,
                            fontSize: '1.3em',
                            fontWeight: 'bold',
                            textAlign: 'center',
                        }}>
                            Admin Access Required
                        </h3>
                        <p style={{
                            color: theme.colors.TEXT_SECONDARY,
                            textAlign: 'center',
                            lineHeight: '1.5',
                            margin: 0,
                            marginBottom: theme.spacing.MEDIUM,
                        }}>
                            You need to login again to access admin features. Your admin roles may have been updated.
                        </p>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: theme.spacing.MEDIUM,
                        }}>
                            <button
                                onClick={() => setShowAdminLoginModal(false)}
                                style={{
                                    background: theme.colors.SURFACE,
                                    border: `1px solid ${theme.colors.BORDER}`,
                                    color: theme.colors.TEXT,
                                    padding: '8px 16px',
                                    borderRadius: theme.borderRadius.SMALL,
                                    cursor: 'pointer',
                                    fontSize: '0.9em',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowAdminLoginModal(false);
                                    // Force a token refresh by signing out and back in
                                    window.location.href = '/login';
                                }}
                                style={{
                                    background: theme.colors.PRIMARY,
                                    border: 'none',
                                    color: '#fff',
                                    padding: '8px 16px',
                                    borderRadius: theme.borderRadius.SMALL,
                                    cursor: 'pointer',
                                    fontSize: '0.9em',
                                    fontWeight: 600,
                                }}
                            >
                                Login Again
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default LeagueComponent; 